import React from "react";
import { Activity, CandlestickChart, LineChart } from "lucide-react";
import TradingChart from "../TradingChart";
import IndicatorsPanel from "../IndicatorsPanel";
import StatTile from "../StatTile";
import EduTooltip from "../EduTooltip";
import FundamentalsCard from "../FundamentalsCard";

const ALL_RANGES = ["1m", "1D", "1W", "1M", "3M", "YTD", "1Y", "5Y"];
const CRYPTO_RANGES = ["1m", "1D", "1W", "1M", "3M", "YTD", "1Y"];
const CHART_MODES = [
  { key: "area", label: "Area", icon: LineChart },
  { key: "line", label: "Line", icon: Activity },
  { key: "candle", label: "Candles", icon: CandlestickChart },
];

const formatNum = (n) => {
  if (n == null) return "—";
  if (Math.abs(n) >= 1e12) return `${(n / 1e12).toFixed(2)}T`;
  if (Math.abs(n) >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(2)}K`;
  return n.toFixed(2);
};

/**
 * Main chart panel for the Asset Detail page: range tabs, chart-mode
 * toggles, candle/area chart, technical indicators, the 4-up stat strip
 * (High/Low/Volume/Mkt Cap), the About card and stock fundamentals.
 *
 * Crypto hides the candle toggle since we only have close prices there, and
 * stocks render the FundamentalsCard at the bottom.
 */
export default function AssetCharts({ data, type, range, setRange, chartMode, setChartMode }) {
  const isUp = (data?.change_pct ?? 0) >= 0;
  const ranges = type === "crypto" ? CRYPTO_RANGES : ALL_RANGES;
  return (
    <>
      <div className="flex items-center gap-2 flex-wrap mb-5">
        <div className="flex items-center gap-1 p-1 rounded-full bg-white border border-[#EAE6E1]" data-testid="range-tabs">
          {ranges.map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              data-testid={`range-${r}`}
              className={`px-4 py-1.5 rounded-full text-xs font-mono-ticker font-semibold transition-soft ${
                range === r ? "bg-[#2A2F2B] text-white" : "text-[#6B7068] hover:text-[#2A2F2B]"
              }`}
            >
              {r}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 p-1 rounded-full bg-white border border-[#EAE6E1]" data-testid="chart-mode-tabs">
          {CHART_MODES.map((m) => {
            if (m.key === "candle" && type === "crypto") return null;
            const Icon = m.icon;
            return (
              <button
                key={m.key}
                onClick={() => setChartMode(m.key)}
                data-testid={`chart-mode-${m.key}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-soft ${
                  chartMode === m.key ? "bg-[#547A8F] text-white" : "text-[#6B7068] hover:text-[#2A2F2B]"
                }`}
              >
                <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
                <span className="hidden sm:inline">{m.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-[#EAE6E1] p-6">
        <TradingChart points={data.points} mode={chartMode} trendUp={isUp} height={420} />
      </div>

      <IndicatorsPanel points={data.points} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          label={<EduTooltip term="High" definition="The highest price the asset reached during this timeframe.">High</EduTooltip>}
          value={`$${formatNum(data.high)}`} testid="stat-high"
        />
        <StatTile
          label={<EduTooltip term="Low" definition="The lowest price the asset reached during this timeframe.">Low</EduTooltip>}
          value={`$${formatNum(data.low)}`} testid="stat-low"
        />
        <StatTile
          label={<EduTooltip term="Volume" definition="Total units traded. Higher volume often signals stronger interest in the asset.">Volume</EduTooltip>}
          value={formatNum(data.volume)} testid="stat-volume"
        />
        <StatTile
          label={<EduTooltip term="Market Cap" definition="The total market value of all shares or coins. A measure of the asset's size.">Mkt Cap</EduTooltip>}
          value={data.info?.market_cap ? `$${formatNum(data.info.market_cap)}` : "—"}
          testid="stat-marketcap"
        />
      </div>

      {data.info?.description && (
        <div className="rounded-2xl bg-white border border-[#EAE6E1] p-6">
          <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068] mb-2">
            About {data.name}
          </div>
          <p className="text-sm text-[#2A2F2B] leading-relaxed">{data.info.description}</p>
        </div>
      )}

      {type === "stock" && <FundamentalsCard symbol={data.symbol} />}
    </>
  );
}
